import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  FaRegLightbulb,
  FaInfoCircle,
  FaSpinner,
  FaTimes,
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaCommentDots,
  FaChevronDown,
  FaChevronUp,
  FaTrashAlt,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import {
  fetchRecommendations,
  selectRecommendations,
  selectDataSummary,
  selectDataGaps,
  setProgress,
  clearProgress,
  selectProgressStep,
  selectProgressMessage,
  clearChatHistory,
  selectRequestInProgress,
  setRequestInProgress,
} from "../agentSlice";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import RecommendationCard from "./RecommendationCard";
import { agentAPI, consumeSSEStream } from "../agentAPI";
import ProgressSteps from "./ProgressSteps";

const safeRender = (val) => {
  if (typeof val === "string" || typeof val === "number") return val;
  if (typeof val === "object" && val !== null) return val.title || val.text || val.recommendation || JSON.stringify(val);
  return "";
};

const AIDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const sessionUser = useSelector(selectLogInUsername);
  const clientId = sessionUser ? sessionUser.split("@")[0] : null;

  const recommendations = useSelector(selectRecommendations);
  const dataSummary = useSelector(selectDataSummary);
  const dataGaps = useSelector(selectDataGaps);
  const loading = useSelector((state) => state.agent.loadingRecommendations);
  const error = useSelector((state) => state.agent.errorRecommendations);
  const requestInProgress = useSelector(selectRequestInProgress);

  const progressStep = useSelector(selectProgressStep);
  const progressMessage = useSelector(selectProgressMessage);

  const [activeTab, setActiveTab] = useState("insights");
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [queueText, setQueueText] = useState("");

  // Ask AI local state
  const [chatMessages, setChatMessages] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasContext, setHasContext] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch recommendations with SSE
  const loadRecommendations = useCallback(async () => {
    if (!clientId) return;
    dispatch(setRequestInProgress(true));
    dispatch(setProgress({ step: 0, message: "Starting analysis..." }));

    try {
      const streamUrl = agentAPI.streamRecommendations(clientId);
      await consumeSSEStream(streamUrl, {
        method: "GET",
        onProgress: (step, message) => {
          dispatch(setProgress({ step, message }));
        },
        onResult: (data) => {
          dispatch(clearProgress());
          dispatch({
            type: "agent/fetchRecommendations/fulfilled",
            payload: data,
          });
          dispatch(setRequestInProgress(false));
        },
        onError: (error) => {
          dispatch(clearProgress());
          console.error("Stream error:", error);
        },
      });
    } catch (err) {
      dispatch(clearProgress());
      dispatch(fetchRecommendations(clientId));
    }
  }, [clientId, dispatch]);

  useEffect(() => {
    if (isOpen && clientId) {
      // If we already have recommendations, show them immediately
      if (recommendations.length > 0) {
        // Show existing recommendations, don't refetch
        return;
      }

      // Only fetch if we have no recommendations and no request is in progress
      if (!requestInProgress) {
        dispatch({ type: "agent/fetchRecommendations/pending" });
        loadRecommendations();
      }
    }
  }, [isOpen, clientId, loadRecommendations, dispatch, recommendations, requestInProgress]);

  // Queue position polling
  useEffect(() => {
    let intervalId;
    if (isTyping && clientId) {
      const fetchQueueStatus = async () => {
        try {
          const res = await agentAPI.getQueueStatus();
          const status = res.data;
          if (status) {
            const queued = status.queued || [];
            const active = status.active;
            
            // Check if we are active or queued
            if (active && active.client === clientId) {
              setQueueText("Processing your request...");
            } else {
              const myIndex = queued.findIndex(q => q.client === clientId);
              if (myIndex >= 0) {
                const ahead = myIndex;
                if (ahead === 0) {
                  setQueueText("Your question is next in line — processing soon");
                } else {
                  setQueueText(`Your question is in queue — ${ahead} request${ahead > 1 ? "s" : ""} ahead of you`);
                }
              } else {
                if (queued.length > 0) {
                  setQueueText(`Queue is busy — ${queued.length} request${queued.length > 1 ? "s" : ""} in line`);
                } else {
                  setQueueText("");
                }
              }
            }
          }
        } catch (err) {
          console.error("Error fetching queue status:", err);
        }
      };
      
      fetchQueueStatus();
      intervalId = setInterval(fetchQueueStatus, 5000);
    } else {
      setQueueText("");
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isTyping, clientId]);

  // Lock body scroll when open & toggle active class
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("ai-agent-active");
    } else {
      document.body.style.overflow = "";
      document.body.classList.remove("ai-agent-active");
    }
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("ai-agent-active");
    };
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Auto-scroll chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, isTyping]);

  // Focus input when switching to Ask AI tab
  useEffect(() => {
    if (activeTab === "ask" && isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [activeTab, isOpen]);

  // Handle ask submission with streaming
  const handleSend = useCallback(
    async (e, customText = null) => {
      if (e) e.preventDefault();
      const queryText = (customText || inputVal).trim();
      if (!queryText || !clientId || isTyping) return;

      setInputVal("");

      const userMsg = {
        id: `user_${Date.now()}`,
        sender: "user",
        text: queryText,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const agentMsgId = `agent_${Date.now()}`;
      const agentMsg = {
        id: agentMsgId,
        sender: "agent",
        text: "",
        isStreaming: true,
        progressStep: 0,
        progressMessage: "Initializing AI...",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setChatMessages((prev) => [...prev, userMsg, agentMsg]);
      setIsTyping(true);

      let accumulatedText = "";

      try {
        const streamUrl = agentAPI.streamAsk();
        await consumeSSEStream(streamUrl, {
          method: "POST",
          body: {
            collectionName: clientId,
            question: queryText,
            currentPage: location.pathname,
          },
          onProgress: (step, message) => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      progressStep: step,
                      progressMessage: message,
                    }
                  : m
              )
            );
          },
          onResult: (data) => {
            accumulatedText = data.answer || "No reply received.";
            const followUps = data.followUpQuestions || [];
            if (data.hasContext) {
              setHasContext(true);
            }
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      text: accumulatedText,
                      followUpQuestions: followUps,
                      isStreaming: false,
                    }
                  : m
              )
            );
            setIsTyping(false);
          },
          onError: (errorMsg) => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      text: `Error: ${errorMsg}`,
                      isStreaming: false,
                    }
                  : m
              )
            );
            setIsTyping(false);
          },
        });

        // Refresh recommendations after a response
        dispatch(fetchRecommendations(clientId));
      } catch (err) {
        console.error("Drawer chat error:", err);
        setChatMessages((prev) =>
          prev.map((m) =>
            m.id === agentMsgId
              ? {
                  ...m,
                  text: `Failed to communicate with agent. ${err.message || ""}`,
                  isStreaming: false,
                }
              : m
          )
        );
        setIsTyping(false);
      }
    },
    [inputVal, clientId, isTyping, location.pathname, dispatch]
  );

  const handleClearHistory = () => {
    setChatMessages([]);
    setHasContext(false);
    dispatch(clearChatHistory());
    agentAPI.clearConversation(clientId);
  };

  const SUGGESTED_QUESTIONS = [
    "What is my biggest conversion problem?",
    "Which pages need the most attention?",
    "How are my nudges performing?",
    "Where are users dropping off?",
  ];

  if (!clientId) return null;

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/25 backdrop-blur-[2px] transition-all duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel - Responsive sizes */}
      <div
        className={`fixed top-0 right-0 h-full z-[61] flex transition-transform duration-300 pointer-events-auto w-full sm:w-[380px] lg:w-[420px]`}
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
        }}
      >
        {/* Left glow edge */}
        <div className="w-[1px] h-full bg-gradient-to-b from-cyan-400/40 via-purple-400/20 to-cyan-400/40 shrink-0" />

        {/* Main drawer body */}
        <div className="w-full h-full flex flex-col overflow-hidden">
          {/* ─── Header ─── */}
          <div className="shrink-0 px-5 pt-5 pb-0">
            {/* Title bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                  <HiSparkles className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-cyan-600 dark:text-cyan-500 leading-tight">
                    AI Insights
                  </h2>
                  <p className="text-[10px] text-slate-400 font-medium tracking-wide uppercase">
                    Powered by CausalFunnel AI
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {activeTab === "insights" && (
                  <button
                    onClick={() => {
                      dispatch({ type: "agent/fetchRecommendations/pending" });
                      loadRecommendations();
                    }}
                    disabled={requestInProgress || loading}
                    className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-all duration-200"
                    title="Refresh insights"
                  >
                    🔄 Refresh
                  </button>
                )}
                {activeTab === "ask" && chatMessages.length > 0 && (
                  <button
                    onClick={handleClearHistory}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-200"
                    title="Clear Chat"
                  >
                    <FaTrashAlt className="text-xs" />
                  </button>
                )}
                {/* Mobile Back Arrow Button */}
                <button
                  onClick={onClose}
                  className="sm:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 text-lg font-bold"
                >
                  ←
                </button>
                {/* Desktop close button */}
                <button
                  onClick={onClose}
                  className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            {/* Tab switcher */}
            <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setActiveTab("insights")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === "insights"
                    ? "bg-cyan-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <FaRegLightbulb
                  className={
                    activeTab === "insights"
                      ? "text-white"
                      : "text-slate-400"
                  }
                />
                Insights
                {recommendations.length > 0 && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      activeTab === "insights"
                        ? "bg-white/20 text-white"
                        : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {recommendations.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("ask")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === "ask"
                    ? "bg-cyan-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <FaCommentDots
                  className={
                    activeTab === "ask" ? "text-white" : "text-slate-400"
                  }
                />
                Ask AI
              </button>
            </div>

            {/* Separator */}
            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mt-4" />
          </div>

          {/* ─── Tab Content ─── */}
          {activeTab === "insights" ? (
            /* ─── INSIGHTS TAB ─── */
            <div className="flex-grow overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
              {/* Data Summary (Starts Collapsed by default) */}
              {dataSummary && (
                <div className="bg-gradient-to-r from-cyan-50/80 to-blue-50/80 rounded-xl border border-cyan-100/60 text-xs overflow-hidden transition-all duration-300">
                  <button
                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                    className="w-full flex items-center justify-between p-3.5 text-left font-bold text-slate-500 uppercase tracking-wider text-[10px] hover:bg-cyan-100/30 transition-colors duration-200 focus:outline-none"
                  >
                    <span>Data Summary</span>
                    {isSummaryExpanded ? (
                      <FaChevronUp className="text-xs text-slate-400" />
                    ) : (
                      <FaChevronDown className="text-xs text-slate-400" />
                    )}
                  </button>
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isSummaryExpanded ? "max-h-[500px] opacity-100 px-3.5 pb-3.5" : "max-h-0 opacity-0 pointer-events-none"
                    }`}
                  >
                    <p className="text-slate-700 leading-relaxed font-medium">
                      {safeRender(dataSummary)}
                    </p>
                  </div>
                </div>
              )}

              {/* Progress Steps / Loader */}
              {(loading || progressStep) && recommendations.length === 0 ? (
                progressStep !== null ? (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60">
                    <ProgressSteps currentStep={progressStep} currentMessage={progressMessage} />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <FaSpinner className="animate-spin text-2xl mb-3 text-cyan-500" />
                    <span className="text-xs font-semibold">
                      Analyzing your data...
                    </span>
                  </div>
                )
              ) : error ? (
                <div className="p-4 text-center text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl space-y-2">
                  <p>{safeRender(error)}</p>
                  <button
                    onClick={loadRecommendations}
                    className="px-3.5 py-1.5 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors text-[11px]"
                  >
                    Try Again
                  </button>
                </div>
              ) : (recommendations.length === 0 && !loading && !progressStep) ? (
                <div className="p-10 text-center text-slate-400 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
                  <span className="text-3xl mb-2 block">🎉</span>
                  <p className="text-sm font-semibold mb-1 text-slate-600">
                    Looking good!
                  </p>
                  <p className="text-xs">
                    No critical issues found right now.
                  </p>
                </div>
              ) : (
                recommendations.map((rec, index) => (
                  <div
                    key={rec.id}
                    className="animate-in fade-in slide-in-from-bottom-3 duration-300"
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <RecommendationCard
                      recommendation={rec}
                      clientId={clientId}
                    />
                  </div>
                ))
              )}
            </div>
          ) : (
            /* ─── ASK AI TAB ─── */
            <div className="flex-grow flex flex-col overflow-hidden">
              {/* Context Memory Indicator */}
              {hasContext && chatMessages.length > 0 && (
                <div className="px-5 py-1.5 bg-slate-100 border-b border-slate-200/60 flex items-center gap-1 shrink-0">
                  <span className="text-[10px] text-slate-500 font-medium">
                    🧠 Remembers previous messages
                  </span>
                </div>
              )}

              {/* Chat messages */}
              <div className="flex-grow overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
                {chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
                      <FaRobot className="text-2xl text-white" />
                    </div>
                    <h5 className="font-bold text-slate-700 text-sm mb-2">
                      Ask me anything about your site
                    </h5>
                    <p className="text-xs text-slate-400 max-w-[240px] leading-relaxed mb-4">
                      I can analyze your traffic, conversions, A/B tests, nudges, heatmaps, and more.
                    </p>
                    {/* Suggested Questions */}
                    <div className="space-y-2 w-full max-w-[280px] text-left">
                      <p className="text-slate-500 text-xs font-semibold px-1">Try asking:</p>
                      {SUGGESTED_QUESTIONS.map((prompt) => (
                        <button
                          key={prompt}
                          onClick={(e) => handleSend(e, prompt)}
                          className="w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/30 text-xs text-slate-600 hover:text-cyan-700 transition-all duration-200 font-medium"
                        >
                          💬 {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  chatMessages.map((msg) => {
                    const isAgent = msg.sender === "agent";
                    return (
                      <div
                        key={msg.id}
                        className={`flex items-start gap-2.5 ${
                          isAgent ? "" : "flex-row-reverse"
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold ${
                            isAgent
                              ? "bg-gradient-to-br from-cyan-500 to-indigo-600 text-white"
                              : "bg-slate-800 text-white"
                          }`}
                        >
                          {isAgent ? <FaRobot /> : <FaUser />}
                        </div>
                        <div
                          className={`flex flex-col max-w-[80%] ${
                            isAgent ? "items-start" : "items-end"
                          }`}
                        >
                          <div
                            className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm ${
                              isAgent
                                ? "bg-white border border-slate-200/80 text-slate-700 rounded-tl-none"
                                : "bg-gradient-to-r from-cyan-600 to-indigo-600 text-white rounded-tr-none"
                            }`}
                          >
                            {msg.isStreaming ? (
                              <div className="space-y-2">
                                <ProgressSteps
                                  currentStep={msg.progressStep}
                                  currentMessage={msg.progressMessage}
                                />
                                {queueText && (
                                  <div className="text-[10px] text-slate-500 font-semibold bg-slate-50 p-2 rounded border border-slate-200/50 flex items-center gap-1.5 animate-pulse">
                                    <span>⏳</span> {queueText}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p style={{ whiteSpace: "pre-wrap" }}>
                                {safeRender(msg.text) || (
                                  <div className="flex items-center gap-1.5 py-0.5">
                                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                  </div>
                                )}
                              </p>
                            )}
                          </div>

                          {/* Follow up questions block */}
                          {isAgent && msg.followUpQuestions && msg.followUpQuestions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5 w-full">
                              {msg.followUpQuestions.map((q, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={(e) => handleSend(e, typeof q === "string" ? q : q?.text || q?.question)}
                                  className="text-left px-3 py-1.5 rounded-full border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/40 text-[10px] text-slate-600 hover:text-cyan-700 transition-all duration-200 font-semibold bg-white/85 shadow-sm"
                                >
                                  💬 {typeof q === "string" ? q : q?.text || q?.question || "Suggested Action"}
                                </button>
                              ))}
                            </div>
                          )}

                          <span className="text-[9px] text-slate-400 mt-1 px-1">
                            {msg.timestamp}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input bar */}
              <div className="shrink-0 px-5 py-3 border-t border-slate-200/80 bg-white/60">
                <form onSubmit={handleSend} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    placeholder={isTyping ? "Still thinking..." : "Ask about your site data..."}
                    disabled={isTyping}
                    className="flex-grow px-4 py-2.5 bg-slate-100 border-0 outline-none rounded-xl text-xs text-slate-800 placeholder-slate-400 disabled:opacity-60 focus:ring-2 focus:ring-cyan-500/30 focus:bg-white transition-all duration-200"
                  />
                  <button
                    type="submit"
                    disabled={!inputVal.trim() || isTyping}
                    className="px-4 py-2.5 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 text-white rounded-xl shadow-sm hover:shadow-md disabled:opacity-40 disabled:hover:shadow-sm transition-all duration-200 shrink-0 min-w-[80px]"
                  >
                    {isTyping ? (
                      <div className="flex items-center gap-1.5">
                        <FaSpinner className="animate-spin text-xs" />
                        <span className="text-[10px] font-semibold">Thinking...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <FaPaperPlane className="text-[11px]" />
                        <span className="text-[10px] font-semibold">Send</span>
                      </div>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { AIDrawer };
export default AIDrawer;
